// StatsComparison.jsx - Compact Modern Comparison
// Clean, space-efficient versus-style comparison

import { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import {
    ArrowLeft, Trophy, TrendingUp, TrendingDown,
    Phone, PhoneForwarded, PhoneOff, Bot, Users,
    Mic, Clock, Activity, Zap, Target, Check, AlertCircle
} from 'lucide-react'
import {
    RadarChart, Radar, PolarGrid, PolarAngleAxis,
    ResponsiveContainer
} from 'recharts'
import { monitoringApi } from '../services/api'
import './StatsComparison.css'

const DISPOSITION_LABELS = {
    'transfer-call': 'Transfers', 'call-transfered': 'Transferred',
    'not-interested': 'Not Interested', 'not_interested': 'Not Interested',
    'answering-machine': 'Ans. Machine', 'answering_machine': 'Ans. Machine',
    'do-not-call': 'DNC', 'do_not_call': 'DNC',
    'no-answer': 'No Answer', 'no_answer': 'No Answer',
    'call-back': 'Callback', 'hung-up': 'Hung Up',
}

const formatLabel = (str) => DISPOSITION_LABELS[str] || str?.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown'
const formatDuration = (s) => s >= 60 ? `${Math.floor(s / 60)}m ${Math.round(s % 60)}s` : `${Math.round(s)}s`
const formatNum = (n) => n >= 1000 ? (n / 1000).toFixed(1) + 'K' : n?.toString() || '0'

const COLORS = ['#ef4444', '#3b82f6', '#22c55e', '#f59e0b']

export default function StatsComparison({ mode, items, dateParams, onBack }) {
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState([])

    const fetchData = useCallback(async () => {
        setLoading(true)
        try {
            const results = await Promise.all(
                items.map(async (item, idx) => {
                    const params = { ...dateParams }
                    const id = item.id || item.sip_id
                    if (mode === 'dialer') params.dialer_id = id
                    else if (mode === 'avatar') params.avatar_id = id
                    else params.sip_id = id

                    const [overview, dispos, avatars, voiceSets, bots] = await Promise.allSettled([
                        monitoringApi.getStatsOverview(params),
                        monitoringApi.getDispositionStats(params),
                        monitoringApi.getStatsByAvatar(params),
                        monitoringApi.getStatsByVoiceSet(params),
                        monitoringApi.getAllStatus()
                    ])

                    const ov = overview.status === 'fulfilled' ? (overview.value.data?.overview || overview.value.data) : {}
                    const dp = dispos.status === 'fulfilled' ? dispos.value.data : {}
                    const av = avatars.status === 'fulfilled' ? (avatars.value.data?.avatars || []) : []
                    const vs = voiceSets.status === 'fulfilled' ? (voiceSets.value.data?.voice_sets || []) : []

                    let botsList = []
                    if (bots.status === 'fulfilled') {
                        const all = bots.value.data || []
                        if (mode === 'dialer') botsList = all.filter(b => b.dialer_id === id)
                        else if (mode === 'sip') botsList = all.filter(b => b.sip_id === id)
                        else botsList = all.filter(b => b.avatar_id === id)
                    }

                    const totalCalls = ov.total_calls || 0
                    const transfers = dp.transfers?.count || 0
                    const transferRate = totalCalls > 0 ? (transfers / totalCalls) * 100 : 0
                    const avgDuration = ov.avg_duration || 0
                    const avgSteps = ov.avg_steps || 0

                    const score = Math.min(100, Math.round(
                        (transferRate * 3) + (Math.min(avgDuration / 60, 2) * 15) +
                        (Math.min(avgSteps, 5) * 6) + (totalCalls > 100 ? 10 : totalCalls / 10)
                    ))

                    return {
                        id, name: item.name || item.sip_id, color: COLORS[idx], idx,
                        totalCalls, transfers, transferRate: +transferRate.toFixed(1),
                        avgDuration, avgSteps: +avgSteps.toFixed(1), score,
                        amCount: ov.answering_machines?.count || 0,
                        amRate: ov.answering_machines?.percentage || 0,
                        niCount: ov.not_interested?.count || 0,
                        niRate: ov.not_interested?.percentage || 0,
                        dispositions: dp.dispositions || [],
                        dispoMap: (dp.dispositions || []).reduce((a, d) => ({ ...a, [d.disposition]: d }), {}),
                        avatars: av, voiceSets: vs,
                        totalBots: botsList.length,
                        activeBots: botsList.filter(b => ['in_call', 'idle', 'online'].includes(b.status)).length,
                        inCallBots: botsList.filter(b => b.status === 'in_call').length,
                    }
                })
            )
            setData(results)
        } catch (e) {
            console.error(e)
        } finally {
            setLoading(false)
        }
    }, [items, dateParams, mode])

    useEffect(() => { items.length >= 2 && fetchData() }, [fetchData, items.length])

    const winner = (metric, higher = true) => {
        const vals = data.map(d => d[metric] || 0)
        const best = higher ? Math.max(...vals) : Math.min(...vals)
        const idx = vals.indexOf(best)
        const other = higher ? Math.min(...vals) : Math.max(...vals)
        const pct = other > 0 ? Math.round(Math.abs(best - other) / other * 100) : (best > 0 ? 100 : 0)
        return { idx, pct }
    }

    const allDispos = [...new Set(data.flatMap(d => d.dispositions.map(x => x.disposition)))]

    const radarData = [
        { m: 'Volume', ...data.reduce((a, d) => ({ ...a, [d.name]: Math.min(100, d.totalCalls / (Math.max(...data.map(x => x.totalCalls)) || 1) * 100) }), {}) },
        { m: 'Transfers', ...data.reduce((a, d) => ({ ...a, [d.name]: d.transferRate * 10 }), {}) },
        { m: 'Duration', ...data.reduce((a, d) => ({ ...a, [d.name]: Math.min(100, d.avgDuration / (Math.max(...data.map(x => x.avgDuration)) || 1) * 100) }), {}) },
        { m: 'Steps', ...data.reduce((a, d) => ({ ...a, [d.name]: Math.min(100, d.avgSteps / (Math.max(...data.map(x => x.avgSteps)) || 1) * 100) }), {}) },
        { m: 'Quality', ...data.reduce((a, d) => ({ ...a, [d.name]: d.score }), {}) },
    ]

    if (loading) {
        return <div className="comp-page"><div className="comp-loading"><div className="spinner" /><p>Analyzing...</p></div></div>
    }

    const scoreWinner = winner('score')
    const sorted = [...data].sort((a, b) => b.score - a.score)

    return (
        <div className="comp-page">
            {/* Header */}
            <div className="comp-header">
                <button className="back-btn" onClick={onBack}><ArrowLeft size={18} /></button>
                <div>
                    <h1>{mode === 'dialer' ? 'Dialer' : mode === 'avatar' ? 'Avatar' : 'SIP'} Comparison</h1>
                    <span className="comp-date">{dateParams.from} → {dateParams.to}</span>
                </div>
            </div>

            {/* Item Headers */}
            <div className="comp-items" style={{ '--cols': data.length }}>
                {data.map((d, i) => (
                    <motion.div
                        key={d.id}
                        className={`comp-item ${scoreWinner.idx === i ? 'winner' : ''}`}
                        style={{ '--c': d.color }}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                    >
                        <div className="item-bar" />
                        {scoreWinner.idx === i && <span className="winner-tag"><Trophy size={12} /> Best</span>}
                        <span className="item-name">{d.name}</span>
                        <div className="item-score">
                            <svg viewBox="0 0 36 36" className="score-ring">
                                <circle cx="18" cy="18" r="16" fill="none" stroke="var(--border-color)" strokeWidth="2" />
                                <circle cx="18" cy="18" r="16" fill="none" stroke={d.color} strokeWidth="2.5"
                                    strokeDasharray={`${d.score} 100`} strokeLinecap="round" transform="rotate(-90 18 18)" />
                            </svg>
                            <span className="score-val">{d.score}</span>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Quick Stats Grid */}
            <div className="comp-section">
                <h3>Performance</h3>
                <div className="comp-grid">
                    <Row label="Total Calls" icon={<Phone size={14} />} data={data} k="totalCalls" fmt={formatNum} w={winner('totalCalls')} />
                    <Row label="Transfers" icon={<PhoneForwarded size={14} />} data={data} k="transfers" fmt={formatNum} w={winner('transfers')} hl />
                    <Row label="Transfer Rate" icon={<Target size={14} />} data={data} k="transferRate" fmt={v => v + '%'} w={winner('transferRate')} hl />
                    <Row label="Avg Duration" icon={<Clock size={14} />} data={data} k="avgDuration" fmt={formatDuration} w={winner('avgDuration')} />
                    <Row label="Avg Steps" icon={<Activity size={14} />} data={data} k="avgSteps" fmt={v => v.toFixed(1)} w={winner('avgSteps')} />
                </div>
            </div>

            {/* Dispositions */}
            <div className="comp-section">
                <h3>Dispositions</h3>
                <div className="comp-grid">
                    <Row label="Ans. Machine" icon={<Bot size={14} />} data={data} k="amRate" fmt={v => v + '%'} w={winner('amRate', false)} low />
                    <Row label="Not Interested" icon={<PhoneOff size={14} />} data={data} k="niRate" fmt={v => v + '%'} w={winner('niRate', false)} low />
                    {allDispos.filter(d => !d.includes('answer') && !d.includes('interest')).slice(0, 6).map(dispo => {
                        const vals = data.map(x => x.dispoMap[dispo]?.percentage || 0)
                        const isXfer = dispo.includes('transfer')
                        const best = isXfer ? Math.max(...vals) : Math.min(...vals)
                        const w = { idx: vals.indexOf(best), pct: 0 }
                        return <Row key={dispo} label={formatLabel(dispo)} data={data} custom={vals} fmt={v => v + '%'} w={w} low={!isXfer} />
                    })}
                </div>
            </div>

            {/* Bots (Dialer mode) */}
            {mode === 'dialer' && (
                <div className="comp-section">
                    <h3>Bots</h3>
                    <div className="comp-grid">
                        <Row label="Total Bots" icon={<Users size={14} />} data={data} k="totalBots" w={winner('totalBots')} />
                        <Row label="Active" icon={<Zap size={14} />} data={data} k="activeBots" w={winner('activeBots')} hl />
                        <Row label="In Call" icon={<Phone size={14} />} data={data} k="inCallBots" w={winner('inCallBots')} />
                    </div>
                    {/* Utilization bars */}
                    <div className="util-row">
                        <span className="util-label">Utilization</span>
                        {data.map((d, i) => {
                            const pct = d.totalBots > 0 ? Math.round(d.activeBots / d.totalBots * 100) : 0
                            const best = Math.max(...data.map(x => x.totalBots > 0 ? x.activeBots / x.totalBots * 100 : 0))
                            return (
                                <div key={i} className={`util-cell ${pct === best && pct > 0 ? 'win' : ''}`}>
                                    <div className="util-bar"><div style={{ width: pct + '%', background: d.color }} /></div>
                                    <span>{pct}%</span>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}

            {/* Avatars & Voice Sets */}
            <div className="comp-row-2">
                <div className="comp-section mini">
                    <h3><Users size={14} /> Avatars</h3>
                    <div className="entity-cols" style={{ '--cols': data.length }}>
                        {data.map((d, i) => (
                            <div key={i} className="entity-col" style={{ '--c': d.color }}>
                                <span className="entity-count">{d.avatars.length}</span>
                                {d.avatars.slice(0, 3).map((a, j) => (
                                    <div key={j} className="entity-row">
                                        <span className="ent-name">{a.avatar_name?.slice(0, 15) || `#${a.avatar_id}`}</span>
                                        <span className={`ent-rate ${a.transfer_rate >= 5 ? 'g' : a.transfer_rate >= 2 ? 'm' : 'l'}`}>{a.transfer_rate}%</span>
                                    </div>
                                ))}
                                {d.avatars.length > 3 && <span className="ent-more">+{d.avatars.length - 3}</span>}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="comp-section mini">
                    <h3><Mic size={14} /> Voice Sets</h3>
                    <div className="entity-cols" style={{ '--cols': data.length }}>
                        {data.map((d, i) => (
                            <div key={i} className="entity-col" style={{ '--c': d.color }}>
                                <span className="entity-count">{d.voiceSets.length}</span>
                                {d.voiceSets.slice(0, 3).map((v, j) => (
                                    <div key={j} className="entity-row">
                                        <span className="ent-name">{v.voice_set_name?.slice(0, 15) || `#${v.voice_set_id}`}</span>
                                        <span className={`ent-rate ${v.transfer_rate >= 5 ? 'g' : v.transfer_rate >= 2 ? 'm' : 'l'}`}>{v.transfer_rate}%</span>
                                    </div>
                                ))}
                                {d.voiceSets.length > 3 && <span className="ent-more">+{d.voiceSets.length - 3}</span>}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Radar + Verdict */}
            <div className="comp-row-2">
                <div className="comp-section mini radar-sec">
                    <h3>Performance Radar</h3>
                    <ResponsiveContainer width="100%" height={180}>
                        <RadarChart data={radarData}>
                            <PolarGrid stroke="var(--border-color)" />
                            <PolarAngleAxis dataKey="m" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                            {data.map(d => (
                                <Radar key={d.id} dataKey={d.name} stroke={d.color} fill={d.color} fillOpacity={0.15} strokeWidth={2} />
                            ))}
                        </RadarChart>
                    </ResponsiveContainer>
                    <div className="radar-legend">
                        {data.map(d => <span key={d.id}><i style={{ background: d.color }} />{d.name}</span>)}
                    </div>
                </div>

                <div className="comp-section mini verdict-sec">
                    <h3><Trophy size={14} /> Verdict</h3>
                    <div className="verdict-list">
                        {sorted.map((d, rank) => (
                            <div key={d.id} className={`verdict-item ${rank === 0 ? 'win' : ''}`} style={{ '--c': d.color }}>
                                <span className="v-rank">#{rank + 1}</span>
                                <span className="v-name">{d.name}</span>
                                <span className="v-score">{d.score} pts</span>
                                <span className="v-rate">{d.transferRate}% xfer</span>
                            </div>
                        ))}
                    </div>
                    {data.length >= 2 && (
                        <p className="verdict-text">
                            <strong style={{ color: sorted[0].color }}>{sorted[0].name}</strong> leads with {sorted[0].score - sorted[1].score} more points
                            and <span className="success">{((sorted[0].transferRate / (sorted[1].transferRate || 1)) * 100 - 100).toFixed(0)}%</span> better transfer rate.
                        </p>
                    )}
                </div>
            </div>
        </div>
    )
}

// Compact Row Component
function Row({ label, icon, data, k, custom, fmt = v => v, w, hl, low }) {
    const vals = custom || data.map(d => d[k] || 0)
    return (
        <div className="comp-row">
            <span className="row-label">{icon}{label}</span>
            {data.map((d, i) => {
                const isWin = w?.idx === i && vals.some(v => v > 0)
                return (
                    <span key={i} className={`row-val ${isWin ? 'win' : ''} ${hl ? 'hl' : ''}`} style={{ '--c': d.color }}>
                        {fmt(vals[i])}
                        {isWin && <Check size={12} />}
                        {isWin && w.pct > 0 && <small>{low ? <TrendingDown size={10} /> : <TrendingUp size={10} />}{w.pct}%</small>}
                    </span>
                )
            })}
        </div>
    )
}